import { apiRequest } from './client';

export interface SearchSnippet {
  text: string;
}

export interface SearchResult {
  id: string;
  title: string;
  summary: string | null;
  score: number;
  snippets: string[];
  // Additional fields for UI display
  action_date: string | null;  // latest_action_date or introduced_date
  status?: string | null;  // lifecycle status (e.g., "Enacted", "Introduced")
  jurisdiction: string | null;  // jurisdiction name (e.g., "United States")
  jurisdiction_code: string | null;  // jurisdiction code (e.g., "US")
  policy_area: string | null;  // category/policy area
  bill_type: string | null;  // type of bill (hr, s, etc.)
  bill_type_label?: string | null; // human-friendly label like "HR (House Bill)"
  bill_number: string | null;  // bill number for display
  source?: string | null; // underlying data source (regulations, congress_bills, etc.)
  ai_domains?: string[]; // granular AI-domain tags (SOW taxonomy)
  explanation?: Record<string, unknown> | null; // SRS #168: matched terms, phrase hits, BM25, cosine, ranks
  why?: string | null;                          // SRS #168: one sentence for the UI
  // False when the corpus holds the record but not the document: a scanned
  // PDF with no text layer, a publisher that refused us, a source that lists
  // documents without publishing them. The result is real and its link works,
  // but there is nothing to read.
  has_full_text?: boolean;
}

export interface SearchResponse {
  query: string;
  total_results: number;
  results: SearchResult[];
  policy_area_counts?: PolicyAreaItem[];
  jurisdiction_counts?: JurisdictionCountItem[];
  corrections?: Record<string, string> | null; // SRS #168: 'showing results for …'
  ranking?: string | null;                     // bm25 | rrf | lexical
}

export interface SearchRequest {
  query: string;
  size?: number;
  jurisdiction_ids?: string[];
  artifact_types?: string[];
  status_buckets?: string[];   // 'enforced' | 'draft' | 'revoked'
  date_from?: string;          // YYYY-MM-DD
  date_to?: string;            // YYYY-MM-DD
  year?: number;
  exact?: boolean; // SRS #168: no spelling correction
}

export interface SearchFiltersResponse {
  jurisdictions: {
    id: string;
    name: string;
    code: string;
    type?: string;
  }[];
  artifact_types: string[];
}

export interface JurisdictionItem {
  id: string;
  name: string;
  code: string;
  type: string;
}

export interface PolicyAreaItem {
  name: string;
  count: number;
}

export interface JurisdictionCountItem {
  code: string | null;
  name: string | null;
  count: number;
}

/**
 * Search regulations using natural language queries
 * @param payload - Search request containing query and optional size
 * @returns Promise with search results
 */
export async function searchRegulations(payload: SearchRequest): Promise<SearchResponse> {
  return await apiRequest<SearchResponse>('/api/v1/search/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function fetchSearchFilters(): Promise<SearchFiltersResponse> {
  return apiRequest<SearchFiltersResponse>('/api/v1/search/filters');
}

/**
 * Check search service health
 * @returns Promise with health status
 */
export async function checkSearchHealth(): Promise<{ status: string; service: string }> {
  return apiRequest<{ status: string; service: string }>('/api/v1/search/health');
}

/**
 * Get list of jurisdictions for filter UI
 * @returns Promise with list of jurisdictions
 */
export async function getJurisdictions(): Promise<JurisdictionItem[]> {
  return apiRequest<JurisdictionItem[]>('/api/v1/search/jurisdictions');
}

/**
 * Get list of policy areas (categories) for filter UI
 * @returns Promise with list of policy areas
 */
export async function getPolicyAreas(): Promise<PolicyAreaItem[]> {
  return apiRequest<PolicyAreaItem[]>('/api/v1/search/policy-areas');
}

export interface ArtifactTextItem {
  text_type: string; // e.g., "full_text", "bill_text", "engrossed", "abstract"
  text_format: string | null; // e.g., "text/html", "text/plain", "application/pdf"
  content: string | null; // The actual text content
}

export interface RegulationDetail {
  id: string;
  title: string;
  summary: string | null;
  content: string | null;
  content_format: string | null;
  content_type: string | null;
  content_warning?: string | null;
  artifact_texts?: ArtifactTextItem[] | null; // All available artifact texts
  action_date: string | null;
  introduced_date: string | null;
  effective_date: string | null;
  jurisdiction: string | null;
  jurisdiction_code: string | null;
  policy_area: string | null;
  bill_type: string | null;
  bill_type_label?: string | null;
  bill_number: string | null;
  congress: number | null;
  origin_chamber: string | null;
  sponsors: string[] | null;
  committees: string[] | null;
  source_url: string | null;
  publication_date?: string | null;
  source_name?: string | null;
  current_location?: string | null;
  status?: string | null;
  binding_nature?: string | null;
  target_entities?: string | null;
  sector_applicability?: string | null;
}

/**
 * Get full details for a single regulation/bill
 * @param id - Regulation ID
 * @returns Promise with regulation detail
 */
export async function getRegulationDetail(id: string): Promise<RegulationDetail> {
  return apiRequest<RegulationDetail>(`/api/v1/search/regulation/${id}`);
}
